import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "../ui/accordion";
import { MessageCircle } from "lucide-react";
import { waLink } from "../../lib/constants";
import useReveal from "../../hooks/useReveal";

const FAQS = [
  {
    q: "Todo derrame pleural é grave?",
    a: "Não. A gravidade depende da causa, do volume e da velocidade de acúmulo. Derrame por insuficiência cardíaca (a causa mais comum) é tratável com diuréticos. Derrame infeccioso responde a antibióticos. Derrame causado por neoplasia exige manejo oncológico. Cada situação é avaliada individualmente.",
  },
  {
    q: "Preciso de cirurgia?",
    a: "Depende da causa e do volume. Muitos casos podem ser resolvidos apenas com tratamento clínico (antibióticos, diuréticos). A cirurgia é reservada quando há dúvida diagnóstica ou na presença de um derrame pleural complicado. A avaliação individual determina o melhor caminho.",
  },
  {
    q: "Toracocentese é segura?",
    a: "Sim. A incidência de complicações graves é menor que 1%. Quando realizada com auxílio do Ultrassom, o risco é ainda menor. Complicações mais comuns (pneumotórax, sangramento) são raras e geralmente resolvem espontaneamente.",
  },
  {
    q: "Como é feita a análise do líquido pleural?",
    a: "A análise básica é feita com dosagem de LDH, glicose, proteínas e contagem global e diferencial de células. Após isso, aplicamos os critérios de Light para diferenciação entre transudato e exsudato e avaliamos o predomínio celular. De acordo com a suspeita clínica, podem ser solicitados exames adicionais: culturas e bacterioscopia (se suspeita de infecção), citologia oncótica (se suspeita de neoplasia), amilase (se suspeita de pancreatite), triglicerídeos (se suspeita de quilotórax), entre outros.",
  },
  {
    q: "Se for maligno, qual é o prognóstico?",
    a: "O prognóstico depende do tipo de câncer, do estágio e da idade do paciente. Derrame pleural maligno indica metástase, e a discussão com o oncologista assistente faz parte do manejo — que pode melhorar significativamente a qualidade de vida.",
  },
  {
    q: "Qual é o custo da toracocentese e da drenagem?",
    a: "Se o seu convênio não tiver cobertura para o procedimento, entre em contato com a nossa equipe para fazer um orçamento personalizado.",
  },
  {
    q: "Pleurodese impede recorrência definitivamente?",
    a: "A pleurodese (aderência intencional da pleura) pode reduzir a recorrência na maioria dos casos, quando bem indicada. Não impede 100%, mas é a melhor estratégia para evitar novas drenagens recorrentes, especialmente em derrame neoplásico.",
  },
];

export default function FAQ() {
  const { ref, isVisible } = useReveal();

  return (
    <section
      id="faq"
      ref={ref}
      className={`py-24 md:py-32 bg-white reveal-section ${isVisible ? "is-visible" : ""}`}
      data-testid="faq-section"
    >
      <div className="max-w-7xl mx-auto px-4 lg:px-8">
        <div className="max-w-3xl mx-auto text-center mb-16">
          <div className="inline-flex items-center bg-[#00897B]/10 text-[#00897B] rounded-full px-4 py-1.5 text-sm font-semibold mb-4">
            Tire Suas Dúvidas
          </div>
          <h2 className="font-poppins font-bold text-3xl sm:text-4xl text-[#212121] mb-4 leading-tight">
            Dúvidas Sobre Derrame Pleural? <br />
            Respondemos Aqui.
          </h2>
          <p className="text-[#212121]/65 text-base">
            Perguntas reais de pacientes reais — respondidas com honestidade.
          </p>
        </div>

        <div className="max-w-3xl mx-auto">
          <Accordion type="single" collapsible className="space-y-3">
            {FAQS.map((item, idx) => (
              <AccordionItem
                key={idx}
                value={`faq-${idx}`}
                data-testid={`faq-item-${idx + 1}`}
                className="border border-gray-100 rounded-xl bg-[#F5F5F5] px-6 overflow-hidden !border-b"
              >
                <AccordionTrigger
                  data-testid={`faq-trigger-${idx + 1}`}
                  className="font-poppins font-semibold text-[#212121] text-sm md:text-base text-left hover:no-underline hover:text-[#0D47A1] py-5 [&[data-state=open]]:text-[#0D47A1] transition-colors"
                >
                  {item.q}
                </AccordionTrigger>
                <AccordionContent className="text-[#212121]/75 text-sm md:text-base leading-relaxed pb-5">
                  {item.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>

        <div className="text-center mt-12">
          <p className="text-[#212121]/60 text-sm mb-4">
            Sua dúvida não está aqui? Envie mensagem pelo WhatsApp.
          </p>
          <a
            href={waLink()}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="faq-cta-button"
            className="inline-flex items-center gap-2 bg-[#0D47A1] text-white font-poppins font-semibold px-8 py-3.5 rounded-full hover:bg-[#00897B] transition-colors duration-200 shadow-md"
          >
            <MessageCircle size={18} />
            Enviar pelo WhatsApp
          </a>
        </div>
      </div>
    </section>
  );
}
